// The constraint registry: owners register constraints on a scope, cards
// read and follow the combined value of the one type they honour. The
// `shared-constraints` plugin provides an instance of `ConstraintStore` as
// the `constraints` API; tests use it directly.
import {
  CONSTRAINT_TYPES,
  combine,
  constraintScopeKey,
  type Constraint,
  type ConstraintDetail,
  type ConstraintLabel,
  type ConstraintOf,
  type ConstraintType,
  type OwnerId,
  type ConstraintScope,
  type ConstraintScopeKey,
} from './types.ts';

export const CONSTRAINTS_API = 'constraints';
export const CONSTRAINTS_API_VERSION = '1.0.0';

/** Who constrains a scope now, for labels such as the board tab's "Puzzle". */
export interface ConstraintOwner {
  owner: OwnerId;
  label: ConstraintLabel;
  detail?: ConstraintDetail;
}

export interface ConstraintHandle {
  /** Replace this registration's constraints, and optionally its detail. */
  update(constraints: readonly Constraint[], detail?: ConstraintDetail): void;
  /** Remove this registration. Further calls do nothing. */
  release(): void;
  /** Run `action` as this owner: the scope's enforcement lets it through. */
  run<R>(action: () => R): R;
  /** False once released, by the owner or with its whole scope. */
  live(): boolean;
}

export interface ConstraintRegistry {
  register(scope: ConstraintScope, owner: OwnerId, label: ConstraintLabel, constraints: readonly Constraint[], detail?: ConstraintDetail): ConstraintHandle;
  /** The combined value of one type in a scope; the type's default when nothing is registered. */
  current<T extends ConstraintType>(scope: ConstraintScope, type: T): ConstraintOf<T>;
  /** Called with the combined value whenever it changes; returns the unsubscribe. */
  subscribe<T extends ConstraintType>(scope: ConstraintScope, type: T, listener: (value: ConstraintOf<T>) => void): () => void;
  /** The latest registration on a scope, if any. */
  owner(scope: ConstraintScope): ConstraintOwner | undefined;
  /** Called whenever the scope's owner or its label or detail changes; returns the unsubscribe. */
  subscribeOwner(scope: ConstraintScope, listener: (owner: ConstraintOwner | undefined) => void): () => void;
  /** True while an owner of the scope runs an action through its handle. */
  bypassed(scope: ConstraintScope): boolean;
  /** Remove every registration on a scope, e.g. when its tab closes or its game changes. */
  releaseScope(scope: ConstraintScope): void;
}

interface Registration {
  id: number;
  owner: OwnerId;
  label: ConstraintLabel;
  detail?: ConstraintDetail;
  constraints: readonly Constraint[];
}

type Listener = (value: never) => void;

export class ConstraintStore implements ConstraintRegistry {
  private readonly scopes = new Map<ConstraintScopeKey, Registration[]>();
  private readonly listeners = new Map<ConstraintScopeKey, Map<ConstraintType, Set<Listener>>>();
  private readonly ownerListeners = new Map<ConstraintScopeKey, Set<(owner: ConstraintOwner | undefined) => void>>();
  private readonly running = new Map<ConstraintScopeKey, number>();
  private nextId = 1;

  register(scope: ConstraintScope, owner: OwnerId, label: ConstraintLabel, constraints: readonly Constraint[], detail?: ConstraintDetail): ConstraintHandle {
    const key = constraintScopeKey(scope);
    const registration: Registration = { id: this.nextId++, owner, label, detail, constraints: [...constraints] };
    this.change(key, () => this.scopes.set(key, [...(this.scopes.get(key) ?? []), registration]));
    let released = false;
    return {
      update: (next, nextDetail) => {
        if (released || !(this.scopes.get(key) ?? []).includes(registration)) return;
        this.change(key, () => {
          registration.constraints = [...next];
          if (nextDetail !== undefined) registration.detail = nextDetail;
        });
      },
      release: () => {
        if (released) return;
        released = true;
        this.change(key, () => {
          const rest = (this.scopes.get(key) ?? []).filter((r) => r.id !== registration.id);
          if (rest.length) this.scopes.set(key, rest);
          else this.scopes.delete(key);
        });
      },
      live: () => !released && (this.scopes.get(key) ?? []).includes(registration),
      run: <R,>(action: () => R): R => {
        this.running.set(key, (this.running.get(key) ?? 0) + 1);
        try {
          return action();
        } finally {
          const n = (this.running.get(key) ?? 1) - 1;
          if (n > 0) this.running.set(key, n);
          else this.running.delete(key);
        }
      },
    };
  }

  current<T extends ConstraintType>(scope: ConstraintScope, type: T): ConstraintOf<T> {
    return this.combinedOf(constraintScopeKey(scope), type);
  }

  subscribe<T extends ConstraintType>(scope: ConstraintScope, type: T, listener: (value: ConstraintOf<T>) => void): () => void {
    const key = constraintScopeKey(scope);
    const byType = this.listeners.get(key) ?? new Map<ConstraintType, Set<Listener>>();
    this.listeners.set(key, byType);
    const set = byType.get(type) ?? new Set<Listener>();
    byType.set(type, set);
    set.add(listener as Listener);
    return () => {
      set.delete(listener as Listener);
    };
  }

  owner(scope: ConstraintScope): ConstraintOwner | undefined {
    return this.ownerOf(constraintScopeKey(scope));
  }

  subscribeOwner(scope: ConstraintScope, listener: (owner: ConstraintOwner | undefined) => void): () => void {
    const key = constraintScopeKey(scope);
    const set = this.ownerListeners.get(key) ?? new Set();
    this.ownerListeners.set(key, set);
    set.add(listener);
    return () => {
      set.delete(listener);
    };
  }

  bypassed(scope: ConstraintScope): boolean {
    return (this.running.get(constraintScopeKey(scope)) ?? 0) > 0;
  }

  releaseScope(scope: ConstraintScope): void {
    const key = constraintScopeKey(scope);
    if (!this.scopes.has(key)) return;
    this.change(key, () => this.scopes.delete(key));
  }

  // ---- internals -----------------------------------------------------------

  private combinedOf<T extends ConstraintType>(key: ConstraintScopeKey, type: T): ConstraintOf<T> {
    const all: Constraint[] = [];
    for (const r of this.scopes.get(key) ?? []) all.push(...r.constraints);
    return combine(type, all);
  }

  private ownerOf(key: ConstraintScopeKey): ConstraintOwner | undefined {
    const list = this.scopes.get(key);
    const last = list?.[list.length - 1];
    if (!last) return undefined;
    const out: ConstraintOwner = { owner: last.owner, label: last.label };
    if (last.detail !== undefined) out.detail = last.detail;
    return out;
  }

  /** Apply a change to a scope, then notify every listener whose combined value changed. */
  private change(key: ConstraintScopeKey, apply: () => void): void {
    const before = new Map(CONSTRAINT_TYPES.map((t) => [t, JSON.stringify(this.combinedOf(key, t))]));
    const ownerBefore = JSON.stringify(this.ownerOf(key) ?? null);
    apply();
    const byType = this.listeners.get(key);
    for (const type of CONSTRAINT_TYPES) {
      const value = this.combinedOf(key, type);
      if (JSON.stringify(value) === before.get(type)) continue;
      for (const listener of byType?.get(type) ?? []) (listener as (v: unknown) => void)(value);
    }
    const owner = this.ownerOf(key);
    if (JSON.stringify(owner ?? null) !== ownerBefore) for (const listener of this.ownerListeners.get(key) ?? []) listener(owner);
  }
}
